/**
 * Menu data object.
 */
import AdUtils from '@shared/utils/ads';
import ChannelView from '@shared/types/enums/channelView';
import Focus from '@shared/types/enums/focus';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import PressableProps from '@shared/types/props/pressable';
import Sections from '@shared/types/enums/sections';
import Storage from '@storage';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { purgeEvents } from '@shared/store/slices/events.slice';
import { pushRoute } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';

/**
 * Icons for the menu items.
 */
import ChannelsIcon from '@components/atoms/icons/channelsLineupIcon';
import ExitIcon from '@components/atoms/icons/exit';
import HomeIcon from '@components/atoms/icons/home';
import MoreContentIcon from '@components/atoms/icons/moreContent';
import MoviesIcon from '@components/atoms/icons/movies';
import MySpaceIcon from '@components/atoms/icons/mySpace';
import SeriesIcon from '@components/atoms/icons/series';
import SettingsIcon from '@components/atoms/icons/settings';
import ShowsIcon from '@components/atoms/icons/shows';
import { setModal } from '@shared/store/slices/modal.slice';

/**
 * Menu item interface.
 */
interface MenuItem {
  action: () => void;
  ad: () => string;
  Icon?: (props: PressableProps) => JSX.Element;
  modal?: () => void;
  text: string;
}

/**
 * Menu data object that contains the data for the different sections of the menu.
 */
const MenuData: Record<string, MenuItem> = {
  /**
   * Initial menu item:
   * - Action: Push the initial page.
   * - Ad: Get the ad source for the initial page..
   */
  Initial: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.INITIAL, overwrite: true },
        })
      );
    },
    ad: () => AdUtils.getSource(Sections.GUIDE_TV),
    Icon: HomeIcon,
    text: 'COMPONENTS.MENU.HOME',
  },
  /**
   * My space menu item:
   * - Action: Push the my space page if logged in, otherwise show the login modal.
   * - Ad: Get the ad source for the my space page if logged in, otherwise return an empty string.
   */
  MySpace: {
    action: () => {
      const { user } = store.getState();
      store.dispatch(
        pushRoute({
          route: { Page: Pages.MY_SPACE, overwrite: true },
          params: user.isAnonymous
            ? {
                current: {
                  message: TranslationManager.get('PAGES.MY_SPACE.LOGIN'),
                  modal: Modals.ANONYMOUS_GENERIC,
                },
              }
            : {},
        })
      );
    },
    ad: () => {
      const { user } = store.getState();
      if (user.isAnonymous) {
        return '';
      } else {
        return AdUtils.getSource(Sections.MY_RECORDINGS);
      }
    },
    Icon: MySpaceIcon,
    modal: () => {
      const { user } = store.getState();
      if (user.isAnonymous) {
        store.dispatch(setModal(Modals.ANONYMOUS_GENERIC));
      }
    },
    text: 'COMPONENTS.MENU.MY_SPACE',
  },
  /**
   * Recordings menu item:
   * - Action: Push the recordings page if logged in, otherwise show the login modal.
   * - Ad: Get the ad source for the recordings page if logged in, otherwise return an empty string.
   */
  Recordings: {
    action: () => {
      const { user } = store.getState();
      store.dispatch(
        pushRoute({
          route: { Page: Pages.RECORDINGS, overwrite: true },
          params: user.isAnonymous
            ? {
                current: {
                  message: TranslationManager.get('PAGES.RECORDINGS.LOGIN'),
                  modal: Modals.ANONYMOUS_GENERIC,
                },
              }
            : {},
        })
      );
    },
    ad: () => {
      const { user } = store.getState();
      if (user.isAnonymous) {
        return '';
      } else {
        return AdUtils.getSource(Sections.MY_RECORDINGS);
      }
    },
    Icon: MySpaceIcon,
    text: 'COMPONENTS.MENU.RECORDINGS',
  },
  /**
   * Channels menu item:
   * - Action: Displays a selection view if not selected, otherwise push the channels selected page.
   * - Ad: Get the ad source for the channels main page.
   */
  Channels: {
    action: () => {
      const { channels } = store.getState();
      store.dispatch(purgeEvents());
      switch (parseInt(Storage.get('userChannelViewSelection') ?? '')) {
        case ChannelView.Classic:
          store.dispatch(
            pushRoute({
              route: { Page: Pages.CHANNELS_CLASSIC, overwrite: true },
            })
          );
          break;
        case ChannelView.Lineup:
          store.dispatch(
            pushRoute({
              route: { Page: Pages.CHANNELS_LINEUP, overwrite: true },
              params: {
                current: {
                  // Start on the first channel without changing the current
                  // one, which deadman and the zapper rely on
                  channel: channels.favouriteChannels?.[0],
                  focus: Focus.MENU,
                },
              },
            })
          );
          break;
        case ChannelView.Grid:
          store.dispatch(
            pushRoute({
              route: { Page: Pages.CHANNELS_GRID, overwrite: true },
            })
          );
          break;
        default:
          store.dispatch(
            pushRoute({
              route: {
                Page: Pages.CHANNELS_CHOICE,
                overwrite: true,
              },
            })
          );
      }
    },
    ad: () => AdUtils.getSource(Sections.CHANNELS_MAIN),
    Icon: ChannelsIcon,
    text: 'COMPONENTS.MENU.CHANNELS',
  },
  /**
   * Movies menu item:
   * - Action: Push the movies page.
   * - Ad: Get the ad source for the movies page.
   */
  Movies: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.MOVIES, overwrite: true },
        })
      );
    },
    ad: () => AdUtils.getSource(Sections.MOVIES),
    Icon: MoviesIcon,
    text: 'COMPONENTS.MENU.MOVIES',
  },
  /**
   * Series menu item:
   * - Action: Push the series page.
   * - Ad: Get the ad source for the series page.
   */
  Series: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.SERIES, overwrite: true },
        })
      );
    },
    ad: () => AdUtils.getSource(Sections.SERIES),
    Icon: SeriesIcon,
    text: 'COMPONENTS.MENU.SERIES',
  },
  /**
   * Shows menu item:
   * - Action: Push the shows page.
   * - Ad: Get the ad source for the shows page.
   */
  Shows: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.SHOWS, overwrite: true },
        })
      );
    },
    ad: () => AdUtils.getSource(Sections.SHOWS),
    Icon: ShowsIcon,
    text: 'COMPONENTS.MENU.PROGRAMMES',
  },
  /**
   * More content menu item:
   * - Action: Push the more content page.
   * - Ad: Get the ad source for the more content page.
   */
  MoreContent: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.MORE_CONTENT, overwrite: true },
        })
      );
    },
    ad: () => AdUtils.getSource(Sections.MORE_CONTENT),
    Icon: MoreContentIcon,
    text: 'COMPONENTS.MENU.MORE_CONTENT',
  },
  /**
   * Settings menu item:
   * - Action: Push the settings page.
   * - Ad: Return an empty string.
   */
  Settings: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.SETTINGS, overwrite: true },
        })
      );
    },
    ad: () => '',
    Icon: SettingsIcon,
    text: 'COMPONENTS.MENU.SETTINGS',
  },
  /**
   * Exit menu item:
   * - Action: Push the exit page.
   * - Ad: Return an empty string.
   */
  Exit: {
    action: () => {
      store.dispatch(
        pushRoute({
          route: { Page: Pages.EXIT, overwrite: true },
        })
      );
    },
    ad: () => '',
    Icon: ExitIcon,
    modal: () => {
      store.dispatch(setModal(Modals.EXIT_APP));
    },
    text: 'COMPONENTS.MENU.EXIT',
  },
};

export type { MenuItem };
export default MenuData;
